import React, { useState, useEffect } from 'react';
import { Encounter } from '../../types/encounter';
import { Appointment, AppointmentStatus } from '../../types/appointment';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import PetRemindersSection from '../pets/PetRemindersSection';
import AppointmentCreateModal from '../appointments/AppointmentCreateModal';
import { Calendar, Plus, Clock, Calendar as CalendarIcon } from 'lucide-react';

interface FollowUpSectionProps {
  encounter: Encounter | null;
  onUpdate: () => void;
}

const FollowUpSection: React.FC<FollowUpSectionProps> = ({ encounter, onUpdate }) => {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);

  useEffect(() => {
    if (encounter?.owner_id) {
      fetchAppointments();
    }
  }, [encounter?.owner_id]);

  const fetchAppointments = async () => {
    if (!encounter?.owner_id) return;

    try {
      setLoadingAppointments(true);
      const token = localStorage.getItem('access_token');
      const response = await fetch(API_ENDPOINTS.APPOINTMENTS.LIST_BY_PET_OWNER(encounter.owner_id), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for upcoming appointments
        const now = new Date();
        const upcoming = (Array.isArray(data) ? data : [])
          .filter((apt: Appointment) => new Date(apt.appointment_date) > now && apt.status !== AppointmentStatus.CANCELLED)
          .sort((a: Appointment, b: Appointment) => new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime());

        setAppointments(upcoming);
      }
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
    } finally {
      setLoadingAppointments(false);
    }
  };

  if (!encounter) return null;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900">Follow Up & Reminders</h2>
        <p className="text-gray-500 mt-1">Schedule follow-up appointments and manage reminders for {encounter.pet_name}.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Upcoming Appointments */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[600px]">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-blue-600" />
              <h3 className="font-medium text-gray-900">Upcoming Appointments</h3>
            </div>
            <button
              onClick={() => setShowAppointmentModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
            >
              <Plus className="w-4 h-4" />
              Schedule Appointment
            </button>
          </div>

          <div className="p-4 flex-1 overflow-y-auto">
            {loadingAppointments ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : appointments.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <CalendarIcon className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">No upcoming appointments</p>
                <p className="text-xs text-gray-500 mt-1">Schedule a follow-up appointment above</p>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments.map((apt) => (
                  <div key={apt.id} className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all group">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-gray-900 group-hover:text-blue-700 transition-colors">{apt.title}</h4>
                        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                          <div className="flex items-center gap-1.5">
                            <CalendarIcon className="w-4 h-4 text-gray-400" />
                            <span>{new Date(apt.appointment_date).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <span>{new Date(apt.appointment_date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                      <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full border ${apt.status === AppointmentStatus.CONFIRMED ? 'bg-green-50 text-green-700 border-green-200' :
                        apt.status === AppointmentStatus.SCHEDULED ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'
                        }`}>
                        {apt.status}
                      </span>
                    </div>
                    {apt.pets && apt.pets.length > 0 && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                        <span className="text-xs text-gray-500">Pets:</span>
                        <div className="flex flex-wrap gap-1">
                          {apt.pets.map(pet => (
                            <span key={pet.id} className="text-xs bg-white border border-gray-200 text-gray-700 px-2 py-0.5 rounded-md">
                              {pet.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Reminders */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[600px]">
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <h3 className="font-medium text-gray-900">Reminders</h3>
            </div>
          </div>
          <div className="p-4 flex-1 overflow-y-auto">
            <PetRemindersSection petId={encounter.pet_id} petName={encounter.pet_name} />
          </div>
        </div>
      </div>

      <AppointmentCreateModal
        isOpen={showAppointmentModal}
        onClose={() => setShowAppointmentModal(false)}
        onSuccess={() => {
          setShowAppointmentModal(false);
          fetchAppointments();
        }}
        initialPetOwnerId={encounter.owner_id}
        initialPetId={encounter.pet_id}
      />
    </div>
  );
};

export default FollowUpSection;
